import { extractProblemDetails } from "@presentation/common/ErrorHelpers";

/**
 * `DenialResponses.EntitlementLockedType` in OF.Manager. Set as the ProblemDetails
 * `type` on team/project scoped-action denials that failed the entitlement gate.
 */
export const ENTITLEMENT_LOCKED_PROBLEM_TYPE = "urn:omicsflow:problem:entitlement-locked";

/**
 * Job endpoints do not go through `DenialResponses`; they surface the ROP error
 * code as the ProblemDetails `title` and set no `type`. These are the two titles
 * that mean "denied" rather than "invalid".
 */
const JOB_ENTITLEMENT_DENIED_TITLE = "Job.EntitlementDenied";
const JOB_PERMISSION_DENIED_TITLE = "Job.PermissionDenied";

export type DenialKind = "entitlement-locked" | "permission-denied";

export interface Denial {
  kind: DenialKind;
  /**
  ProblemDetails `title`, e.g. "Insufficient permission" or "Job.PermissionDenied".
  */
  title?: string;
  /**
  ProblemDetails `detail`. The backend interpolates the attempted action into this string.
  */
  detail?: string;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const readProblemType = (error: unknown): string | undefined => {
  const data = (error as { response?: { data?: unknown } } | undefined)?.response?.data;
  if (!isObject(data)) return undefined;
  return typeof data.type === "string" ? data.type : undefined;
};

/**
 * Classifies a rejected request as an authorization denial, or `null` if it is
 * anything else (network failure, validation error, 500, ...).
 *
 * The backend emits two structurally different denials and they must not be
 * conflated, because only one of them is actionable by the user:
 *
 * 1. Team/project scoped-action filters (`DenialResponses`) distinguish the two
 *    cases by the ProblemDetails `type` urn. Absence of the urn on a 403 means an
 *    ordinary permission denial.
 * 2. Job endpoints return a ROP failure whose `title` is the error code and which
 *    carries no `type` at all, so a 403 from `POST /jobs` would otherwise be
 *    misread as a permission denial even when it was an entitlement denial.
 *
 * Note that `EntitlementGate.IsEntitled` is currently hardcoded `true` on the
 * backend (TODO(SF-2797)), so the entitlement branch is unreachable at runtime
 * today. It is implemented because reverting that TODO makes it live again with
 * no frontend change.
 */
export const classifyDenial = (error: unknown): Denial | null => {
  const { title, detail, status } = extractProblemDetails(error);

  if (status !== 403) return null;

  if (title === JOB_ENTITLEMENT_DENIED_TITLE || readProblemType(error) === ENTITLEMENT_LOCKED_PROBLEM_TYPE) {
    return { kind: "entitlement-locked", title, detail };
  }

  return title === JOB_PERMISSION_DENIED_TITLE ? { kind: "permission-denied", title, detail } : { kind: "permission-denied", title, detail };
};

export const isEntitlementLocked = (error: unknown): boolean => classifyDenial(error)?.kind === "entitlement-locked";

export const isPermissionDenied = (error: unknown): boolean => classifyDenial(error)?.kind === "permission-denied";
